<template>
  <div>
    <basic-list
        :headline="headline"
        :headlines="headlines"
        :filters="filters"
        :filter-values.sync="filterValues"
        :data="data"
        :pagination="showPagination ? pagination : null"
        :loading="loading"
        :error="error"
        :empty-message="emptyMessage"
        :can-show="canShow"
        :can-destroy="canDestroy"
        :get-row-class="getRowClass"
        :selected.sync="selected"
        :can-destroy-selected="canDestroySelected"
        :show-checkboxes="showCheckboxes"
        :show-buttons="showButtons"
        :show-list-actions="showListActions"
        :reorderable="reorderEndpoint !== null"
        :can-reorder="canReorder"
        :checkbox-column-width="checkboxColumnWidth"
        :button-column-width="buttonColumnWidth"
        :page-size.sync="pageSize"
        :page-size-options="pageSizeOptions"
        @navigate="navigate"
        @show="$emit('show', $event)"
        @destroy="destroy"
        @destroy-selected="destroySelected"
        @clear-filters="clearFilters"
        @reordered="reordered"
        @sort="sort"
    >
      <template v-slot:default="{ row }">
        <slot :row="row">
          <td>
            {{ row.attributes.name }}
          </td>
        </slot>
      </template>

      <template v-slot:list-actions>
        <slot name="list-actions"
              :selected="selected"
              :load="load"
              :update="update"
              :filter-values="filterValues"
              :can-update-selected="canUpdateSelected"
              :reset-selected="resetSelected"
        />

        <li :class="{'disabled': !canDestroySelected}">
          <a href="#" @click.prevent="canDestroySelected && destroySelected()">
            <i class="fa fa-trash"/> Delete selected
          </a>
        </li>

        <template v-if="hasInactive">
          <li :class="{'disabled': !canUpdateSelected}">
            <a href="#" @click.prevent="canUpdateSelected && activateSelected()">
              <i class="fa fa-check"/> Activate selected
            </a>
          </li>

          <li :class="{'disabled': !canUpdateSelected}">
            <a href="#" @click.prevent="canUpdateSelected && deactivateSelected()">
              <i class="fa fa-times"/> Deactivate selected
            </a>
          </li>

          <li class="divider"/>

          <li>
            <a href="#" @click.prevent="toggleInactive">
              <i
                  class="fa"
                  :class="withInactive ? 'fa-eye-slash' : 'fa-eye'"
              ></i> {{ withInactive ? 'Hide' : 'Show' }} inactive
            </a>
          </li>
        </template>
      </template>

      <template v-slot:bottom>
        <slot name="bottom"/>
      </template>

      <template v-slot:headline-right>
        <slot name="headline-right"/>
      </template>
    </basic-list>

    <slot name="modals" :load="load" :update="update" :selected="selected" :reset-selected="resetSelected"/>
  </div>
</template>

<script>
import http from '@/http'
import modal from '@/modal'
import notify from '@/notify'
import BasicList from './Basic'

export default {
  components: {BasicList},

  props: {
    headline: {
      required: false,
      default: null
    },

    headlines: {
      required: false,
      type: Array,
      default: () => [{width: 10, text: 'Name'}]
    },

    filters: {
      required: false,
      type: Array,
      default: () => []
    },

    endpoint: {
      required: true,
      type: String
    },

    endpointFilters: {
      required: false,
      type: Object,
      default: () => ({})
    },

    emptyMessage: {
      required: false,
      type: String,
      default: 'The list is empty.'
    },

    canShow: {
      required: false,
      type: Function,
      default: row => true
    },

    canUpdate: {
      required: false,
      type: Function,
      default: row => row.meta && row.meta.permissions.update
    },

    canDestroy: {
      required: false,
      type: Function,
      default: row => row.meta && row.meta.permissions.delete
    },

    getRowClass: {
      required: false,
      type: Function,
      default: row => ''
    },

    showCheckboxes: {
      required: false,
      type: Boolean,
      default: true
    },

    showButtons: {
      required: false,
      type: Boolean,
      default: true
    },

    showPagination: {
      required: false,
      type: Boolean,
      default: true
    },

    showListActions: {
      required: false,
      type: Boolean,
      default: true
    },

    hasInactive: {
      required: false,
      type: Boolean,
      default: false
    },

    channel: {
      required: false,
      type: String,
      default: null
    },

    createdEvent: {
      required: false,
      type: String,
      default: null
    },

    updatedEvent: {
      required: false,
      type: String,
      default: null
    },

    getRowFromEvent: {
      required: false,
      type: Function
    },

    reorderEndpoint: {
      required: false,
      type: String,
      default: null
    },

    canReorder: {
      required: false,
      type: Boolean,
      default: true
    },

    getReorderPayload: {
      required: false,
      type: Function,
      default: data => ({data})
    },

    getDestroyUrl: {
      required: false,
      type: Function,
      default: row => row.links.self
    },

    checkboxColumnWidth: {
      required: false,
      type: Number,
      default: 1
    },

    buttonColumnWidth: {
      required: false,
      type: Number,
      default: 1
    },

    defaultPageSize: {
      required: false,
      default: 25
    },

    pageSizeOptions: {
      required: false,
      type: Array,
      default: () => [10, 25, 50, 100]
    }
  },

  data() {
    return {
      data: [],
      pagination: null,
      error: null,

      page: 1,
      pageSize: this.defaultPageSize,
      selected: [],

      filterValues: {},

      sortColumn: null,
      sortDirection: null,

      loading: true,
      withInactive: false
    }
  },

  mounted() {
    this.load()

    if (this.channel) {
      this.listen()
    }
  },

  watch: {
    endpointFilters() {
      this.navigate(1)
    },

    pageSize() {
      this.navigate(1)
    }
  },

  computed: {
    canUpdateSelected() {
      return this.selected.length > 0 && this.selected.every(this.canUpdate)
    },

    canDestroySelected() {
      return this.selected.length > 0 && this.selected.every(this.canDestroy)
    },

    requestFilters() {
      let filters = {}

      Object.entries(this.endpointFilters).map(entry => {
        filters['filter[' + entry[0] + ']'] = entry[1]
      })

      Object.entries(this.filterValues).filter(entry => entry[1]).map(entry => {
        if (Array.isArray(entry[1])) {
          return filters['filter[' + entry[0] + ']'] = entry[1].map(tag => tag.text)
        }

        filters['filter[' + entry[0] + ']'] = entry[1]
      })

      if (this.hasInactive) {
        filters['filter[with_inactive]'] = this.withInactive ? 'with' : 'without'
      }

      return filters
    }
  },

  methods: {
    load() {
      this.loading = true

      this.resetSelected()

      http.get(this.endpoint, {
        ...this.requestFilters,
        sort: this.sortColumn ? (this.sortDirection === 'desc' ? '-' : '') + this.sortColumn : null,
        page: this.page,
        page_size: this.pageSize
      })
          .then(response => {
            this.data = response.data.data

            this.pagination = response.data.meta

            this.loading = false
          })
          .catch(error => {
            if (error.response.status === 400) {
              this.error = error.response.data.message

              this.loading = false
            }

            throw error
          })
    },

    add(row) {
      if (this.page === 1) {
        this.data.unshift(row)

        if (this.data.length === this.pageSize + 1) {
          this.data.splice(this.pageSize, 1)
        }
      }
    },

    update(row) {
      let index = this.data.findIndex(existing => existing.id === row.id)

      if (index > -1) {
        this.data.splice(index, 1, row)
      }
    },

    navigate(page) {
      this.page = page

      this.load()
    },

    destroy(row) {
      modal.confirm('Are you sure that you want to delete the item?', () => {
        this.getDestroyPromise(row).then(() => {
          notify.success('The item was deleted')

          this.load()
        }).catch(error => {
          if (error.response.status === 400) {
            notify.error(error.response.data.error)
          }
        })
      })
    },

    destroySelected() {
      modal.confirm('Are you sure that you want to delete the items?', () => {
        Promise.all(this.selected.map(row => this.getDestroyPromise(row))).then(() => {
          notify.success('The items were deleted')

          this.load()
        }).catch(error => {
          if (error.response.status === 400) {
            notify.error(error.response.data.error)

            this.load()
          }
        })
      })
    },

    getDestroyPromise(row) {
      return http.delete(this.getDestroyUrl(row))
    },

    activateSelected() {
      modal.confirm('Are you sure that you want to activate the items?', () => {
        Promise.all(this.selected.map(row => http.put(row.links.self, {
          ...row.attributes,
          is_active: true
        }))).then(() => {
          notify.success('The items were activated')

          this.load()
        })
      })
    },

    deactivateSelected() {
      modal.confirm('Are you sure that you want to deactivate the items?', () => {
        Promise.all(this.selected.map(row => http.put(row.links.self, {
          ...row.attributes,
          is_active: false
        }))).then(() => {
          notify.success('The items were deactivated')

          this.load()
        })
      })
    },

    clearFilters() {
      this.filterValues = {}

      this.navigate(1)
    },

    toggleInactive() {
      this.withInactive = !this.withInactive

      this.navigate(1)
    },

    listen() {
      if (this.createdEvent) {
        Echo.channel(this.channel)
            .listen(this.createdEvent, event => {
              this.add(this.getRowFromEvent(event))
            })
      }

      if (this.updatedEvent) {
        Echo.channel(this.channel)
            .listen(this.updatedEvent, event => {
              this.update(this.getRowFromEvent(event))
            })
      }
    },

    reordered(data) {
      this.data = data

      http.put(this.reorderEndpoint, this.getReorderPayload(data))
          .then(() => {
            notify.success('The items were reordered')
          })
    },

    resetSelected() {
      this.selected = []
    },

    sort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortDirection = 'asc'
      }

      this.sortColumn = column

      this.navigate(1)
    }
  }
}
</script>
